import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';

import { GetZeePersonaDataApiResponse } from '@/actions/zee-persona-data/getZeePersonaData';
import { updateZeePersonaData } from '@/actions/zee-persona-data/updateZeePersonaData';
import { ApiResponse } from '@/types/response.type';
import { UpdateZeePersonaDataPayload } from '@/types/zee-persona-data.type';

export interface UseUpdateZeePersonaDataReturn {
  mutate: (
    userId: string,
    payload: UpdateZeePersonaDataPayload
  ) => Promise<ApiResponse<GetZeePersonaDataApiResponse> | undefined>;
  result: ApiResponse<GetZeePersonaDataApiResponse> | undefined;
  error: Error | null;
  isError: boolean;
  isLoading: boolean;
  isSuccess: boolean;
}

export const useUpdateZeePersonaData = (): UseUpdateZeePersonaDataReturn => {
  const queryClient = useQueryClient();

  const mutation: UseMutationResult<
    ApiResponse<GetZeePersonaDataApiResponse> | undefined,
    Error,
    { userId: string; payload: UpdateZeePersonaDataPayload }
  > = useMutation({
    mutationFn: ({ userId, payload }) => updateZeePersonaData(userId, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['zee-persona-data', variables.userId],
      });
    },
  });

  return {
    mutate: (userId, payload) => mutation.mutateAsync({ userId, payload }),
    result: mutation.data,
    error: mutation.error,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    isLoading: mutation.isPending,
  };
};
